import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import { EditFolderNameModal } from '../components/edit-folder-name-modal';

const RenameFolderModal = ({
  folderId,
  folderName,
  isDialogVisible,
  setDialogVisible,
  renameFolderAsync,
  onSuccess,
  onError,
  i18n,
  logger,
}) => {
  const [errorMessage, setErrorMessage] = useState('');

  const closeDialog = useCallback(() => {
    setDialogVisible(false);
    setErrorMessage('');
  }, [setDialogVisible]);

  const onRenameFolder = useCallback((name) => {
    logger.logTrace('Confirm to rename folder', { folderName, newFolderName: name });
    renameFolderAsync({ folderId, folderName, newFolderName: name }).then(() => {
      setDialogVisible(false);
      onSuccess({ folderId, folderName, newFolderName: name });
      logger.logTrace('rename folder successfully', { folderName, newFolderName: name });
    }).catch((alODataErr) => {
      const { actionable, uxMessage } = alODataErr;
      if (actionable) {
        // prompt user to correct
        setErrorMessage(i18n.getString(uxMessage));
      } else {
        logger.logError(alODataErr, { folderName, newFolderName: name });
        closeDialog();
        onError(i18n.getString(uxMessage));
      }
    });
  }, [closeDialog, folderId, folderName, i18n, logger, onError, onSuccess, renameFolderAsync, setDialogVisible]);

  const onCancel = useCallback(() => {
    closeDialog();
    logger.logTrace('Cancel to rename folder');
  }, [closeDialog, logger]);

  return (
    <EditFolderNameModal
      title={i18n.getString(_TL_('Rename folder'))}
      initName={folderName}
      show={isDialogVisible}
      i18n={i18n}
      onComplete={onRenameFolder}
      onCancel={onCancel}
      errorText={errorMessage}
      setErrorText={setErrorMessage}
    />
  );
};

RenameFolderModal.propTypes = {
  folderId: PropTypes.number,
  folderName: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isDialogVisible: PropTypes.bool.isRequired,
  setDialogVisible: PropTypes.func.isRequired,
  renameFolderAsync: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
};

RenameFolderModal.defaultProps = {
  folderId: undefined,
  folderName: undefined,
};

export const useRenameFolderModal = (i18n, logger) => {
  const [isDialogVisible, setDialogVisible] = useState(false);
  const folderItemRef = useRef();

  const openModal = (item) => {
    folderItemRef.current = item;
    setDialogVisible(true);
  };

  const render = useCallback((renameFolderAsync, onSuccess, onError) => {
    const { folderId, folderName } = folderItemRef.current || {};
    return (<RenameFolderModal
      folderId={folderId}
      folderName={folderName}
      isDialogVisible={isDialogVisible}
      setDialogVisible={setDialogVisible}
      renameFolderAsync={renameFolderAsync}
      onSuccess={onSuccess}
      onError={onError}
      i18n={i18n}
      logger={logger}
    />);
  }, [i18n, isDialogVisible, logger]);

  return {
    open: openModal,
    render,
  };
};
